import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Preferences } from '@capacitor/preferences';
import { isNative } from '../../lib/platform';
import { haptics } from '../../lib/native/haptics';

const PREF_KEY = 'walkthrough_seen';

interface Screen {
  emoji:    string;
  title:    string;
  subtitle: string;
}

const SCREENS: Screen[] = [
  {
    emoji:    '✦',
    title:    'Seu mapa natal completo',
    subtitle: 'Descubra sua carta astral, previsões personalizadas e muito mais, em português, feito para você.',
  },
  {
    emoji:    '💘',
    title:    'Descubra compatibilidade antes do primeiro encontro',
    subtitle: 'Envie uma foto e deixe a astrologia, numerologia e fisionomia falarem antes de você.',
  },
  {
    emoji:    '🌟',
    title:    'Comece agora, é grátis',
    subtitle: 'Acesse o mapa natal, tarô e I Ching sem custo. Upgrade quando quiser.',
  },
];

export function AppWalkthrough() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [screen,  setScreen]  = useState(0);

  useEffect(() => {
    if (!isNative) return; // never shown on web
    Preferences.get({ key: PREF_KEY }).then(({ value }) => {
      if (!value) setVisible(true);
    });
  }, []);

  async function dismiss() {
    await Preferences.set({ key: PREF_KEY, value: 'true' });
    setVisible(false);
  }

  async function advance() {
    await haptics.light();
    if (screen < SCREENS.length - 1) {
      setScreen(s => s + 1);
    }
  }

  async function goRegister() {
    await dismiss();
    navigate('/register');
  }

  async function goLogin() {
    await dismiss();
    navigate('/login');
  }

  if (!visible) return null;

  const current = SCREENS[screen];
  const isLast  = screen === SCREENS.length - 1;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'var(--bg-base)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '2rem',
    }}>
      {/* Skip button */}
      {!isLast && (
        <button
          onClick={dismiss}
          style={{
            position: 'absolute', top: '3rem', right: '1.5rem',
            background: 'none', border: 'none', color: 'var(--text-muted)',
            fontSize: 'var(--text-label)', cursor: 'pointer',
          }}
        >
          Pular
        </button>
      )}

      {/* Illustration */}
      <div style={{
        fontSize: '5rem', marginBottom: '2rem',
        filter: 'drop-shadow(0 0 24px rgba(245,200,66,0.4))',
        fontFamily: 'var(--font-display)',
        color: 'var(--petrol)',
      }}>
        {current.emoji}
      </div>

      {/* Text */}
      <h1 className="font-display" style={{
        fontSize: 'clamp(1.5rem,5vw,2rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '1rem',
        lineHeight: 1.2,
      }}>
        {current.title}
      </h1>
      <p style={{
        color: 'var(--text-secondary)',
        textAlign: 'center',
        fontSize: 'var(--text-body)',
        maxWidth: '320px',
        lineHeight: 1.6,
        marginBottom: '2.5rem',
      }}>
        {current.subtitle}
      </p>

      {/* Dots */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2.5rem' }}>
        {SCREENS.map((_, i) => (
          <div key={i} style={{
            width: i === screen ? '20px' : '8px',
            height: '8px',
            borderRadius: '4px',
            background: i === screen ? 'var(--petrol)' : 'var(--border-subtle)',
            transition: 'all 0.3s ease',
          }} />
        ))}
      </div>

      {/* Actions */}
      {isLast ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', maxWidth: '320px' }}>
          <button className="btn btn-primary" onClick={goRegister} style={{ width: '100%', padding: '0.875rem' }}>
            ✦ Criar conta, é grátis
          </button>
          <button className="btn btn-secondary" onClick={goLogin} style={{ width: '100%' }}>
            Já tenho conta
          </button>
        </div>
      ) : (
        <button className="btn btn-primary" onClick={advance} style={{ padding: '0.875rem 2.5rem' }}>
          Próximo →
        </button>
      )}
    </div>
  );
}
